import { useEffect, useState, useCallback } from "react";

import {
  fetchFieldValueAdders,
  createFieldValueAdder,
  updateFieldValueAdder,
  deleteFieldValueAdder,
} from "../api/FieldValueAdderProfileApi";

import {
  fetchFarms,
  fetchGHSEs,
  fetchBays,
} from "../api/ghseApi";

import { fetchVarieties } from "../api/varietyApi";

export default function useManageFieldValueAdders() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");

  const [farms, setFarms] = useState([]);
  const [ghses, setGhses] = useState([]);
  const [bays, setBays] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [filteredVarieties, setFilteredVarieties] = useState([]);

  const [form, setForm] = useState({
    full_name: "",
    payroll_number: "",
    farms: [],
    ghses: [],
    bays: [],
    varieties: [],
    assignments: [],
  });

  const [editingId, setEditingId] = useState(null);

  // ================= INIT =================
  useEffect(() => {
    loadInitial();
  }, []);

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [profilesData, farmsData, varietiesData] = await Promise.all([
        fetchFieldValueAdders(),
        fetchFarms(),
        fetchVarieties(),
      ]);

      setProfiles(profilesData || []);
      setFarms(farmsData || []);
      setVarieties(varietiesData || []);
    } catch (err) {
      setAssignmentError("Unable to load profiles or farm data. Refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  // ================= INPUT =================
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // ================= DEPENDENCIES =================
  const loadGHSEs = useCallback(async (farmIds) => {
    if (!farmIds?.length) {
      setGhses([]);
      return;
    }

    try {
      const results = await Promise.all(
        farmIds.map((id) => fetchGHSEs(id, null))
      );
      setGhses(results.flat());
    } catch (err) {
      setGhses([]);
      throw err;
    }
  }, []);

  const loadBays = useCallback(async (ghseIds) => {
    if (!ghseIds?.length) {
      setBays([]);
      return;
    }

    try {
      const results = await Promise.all(
        ghseIds.map((id) => fetchBays(id))
      );
      setBays(results.flat());
    } catch (err) {
      setBays([]);
      throw err;
    }
  }, []);

  // Filter varieties by selected bays
  const filterVarietiesByBays = useCallback((bayIds, allVarieties) => {
    if (!bayIds?.length) {
      setFilteredVarieties([]);
      return;
    }

    const filtered = allVarieties.filter((variety) => {
      if (!variety.locations_detail?.length) return false;

      return variety.locations_detail.some((loc) =>
        bayIds.includes(parseInt(loc.bay_id))
      );
    });

    setFilteredVarieties(filtered);
  }, []);

  // ================= MULTI SELECT =================
  const handleMultiSelect = useCallback(async (name, values) => {
    const cleanValues = values.map(Number);

    setForm((prev) => {
      const updated = { ...prev, [name]: cleanValues };

      // Cascade clear dependent fields
      if (name === "farms") {
        updated.ghses = [];
        updated.bays = [];
        updated.varieties = [];
        updated.assignments = [];
      }

      if (name === "ghses") {
        updated.bays = [];
        updated.varieties = [];
        updated.assignments = [];
      }

      if (name === "bays") {
        const previousAssignments = prev.assignments || [];
        updated.assignments = cleanValues.flatMap((bayId) => {
          const bay = bays.find((item) => Number(item.id) === bayId);
          const ghse = ghses.find((item) => Number(item.id) === Number(bay?.ghse));
          const sections = ghse?.requires_upper_lower ? ["upper", "lower"] : ["single"];
          return sections.map((vertical_section) => {
            const existing = previousAssignments.find((row) => Number(row.bay) === bayId && row.vertical_section === vertical_section);
            return {
              bay: bayId,
              vertical_section,
              active: existing ? existing.active !== false : true,
              varieties: existing?.varieties || [],
            };
          });
        });
        updated.varieties = [...new Set(updated.assignments.filter((row) => row.active !== false).flatMap((row) => row.varieties || []).map(Number))];
      }

      return updated;
    });

    // Load dependent data
    if (name === "farms") {
      setGhses([]);
      setBays([]);
      setFilteredVarieties([]);
      await loadGHSEs(cleanValues);
    }

    if (name === "ghses") {
      setBays([]);
      setFilteredVarieties([]);
      await loadBays(cleanValues);
    }

    if (name === "bays") {
      setFilteredVarieties([]);
      filterVarietiesByBays(cleanValues, varieties);
    }
  }, [loadGHSEs, loadBays, filterVarietiesByBays, varieties, bays, ghses]);

  const updateAssignment = useCallback((bayId, verticalSection, changes) => {
    setForm((previous) => {
      const assignments = (previous.assignments || []).map((row) =>
        Number(row.bay) === Number(bayId) && row.vertical_section === verticalSection
          ? { ...row, ...changes }
          : row
      );
      return {
        ...previous,
        assignments,
        varieties: [...new Set(assignments.filter((row) => row.active !== false).flatMap((row) => row.varieties || []).map(Number))],
      };
    });
  }, []);

  // ================= CRUD =================
  const createProfile = async (data = form) => {
    setLoading(true);
    try {
      const response = await createFieldValueAdder(data);
      setProfiles((prev) => [...prev, response]);
      resetForm();
      return response;
    } catch (err) {
      console.error("Create error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (id, data) => {
    setLoading(true);
    try {
      const response = await updateFieldValueAdder(id, data);
      setProfiles((prev) =>
        prev.map((p) => (p.id === id ? response : p))
      );
      setEditingId(null);
      resetForm();
      return response;
    } catch (err) {
      console.error("Update error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProfile = async (id) => {
    setLoading(true);
    try {
      await deleteFieldValueAdder(id);
      setProfiles((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Delete error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ================= RESET =================
  const resetForm = useCallback(() => {
    setForm({
      full_name: "",
      payroll_number: "",
      farms: [],
      ghses: [],
      bays: [],
      varieties: [],
      assignments: [],
    });
    setEditingId(null);
    setGhses([]);
    setBays([]);
    setFilteredVarieties([]);
  }, []);

  // ================= EDIT LOADER =================
  const loadForm = useCallback(async (profile) => {
    setLoading(true);
    setAssignmentError("");
    try {
    setEditingId(profile.id);
    setForm({
      full_name: profile.full_name || "",
      payroll_number: profile.payroll_number || "",
      farms: profile.farms || [],
      ghses: profile.ghses || [],
      bays: profile.bays || [],
      varieties: profile.varieties || [],
      assignments: (profile.assignments || []).map((assignment) => ({
        bay: Number(assignment.bay),
        vertical_section: assignment.vertical_section,
        varieties: (assignment.varieties || []).map(Number),
        active: true,
      })),
    });

    // Load dependent data for edit mode
    if (profile.farms?.length) {
      await loadGHSEs(profile.farms);
    }
    if (profile.ghses?.length) {
      await loadBays(profile.ghses);
    }
    if (profile.bays?.length) {
      filterVarietiesByBays(profile.bays, varieties);
    }
    } catch {
      setAssignmentError("Unable to load this profile’s locations. Reopen the profile to retry before changing assignments.");
    } finally { setLoading(false); }
  }, [loadGHSEs, loadBays, filterVarietiesByBays, varieties]);

  // Prepare form data for submission (clean empty arrays)
  const prepareSubmitData = useCallback(() => {
    const assignments = (form.assignments || []).filter((row) => row.active !== false).map((row) => ({
      bay: Number(row.bay),
      vertical_section: row.vertical_section,
      varieties: (row.varieties || []).map(Number),
    }));
    return {
      ...form,
      farms: form.farms.length ? form.farms : [],
      ghses: [...new Set(assignments.map((assignment) => bays.find((bay) => Number(bay.id) === assignment.bay)?.ghse).filter(Boolean).map(Number))],
      bays: [...new Set(assignments.map((assignment) => assignment.bay))],
      varieties: [...new Set(assignments.flatMap((assignment) => assignment.varieties))],
      assignments,
    };
  }, [form, bays]);

  // Check if form is valid
  const isFormValid = useCallback(() => {
    return (
      form.full_name.trim() &&
      form.payroll_number.trim() &&
      form.farms.length > 0
    );
  }, [form]);

  return {
    // Data
    profiles,
    loading,
    editingId,
    assignmentError,

    // Reference data
    farms,
    ghses,
    bays,
    varieties,
    filteredVarieties,

    // Form
    form,
    setForm,

    // Actions
    handleChange,
    handleMultiSelect,
    updateAssignment,

    // CRUD
    createProfile,
    updateProfile,
    deleteProfile,
    resetForm,

    // Edit mode
    loadForm,
    prepareSubmitData,
    isFormValid,
  };
}
